/**
 * Registry for the mobile calendar⇄inbox touch-drop bridge (touch can't drive
 * HTML5 DnD, so the phone UX hand-rolls it). Two directions:
 *
 *  - inbox → calendar: `TimeAxisGrid` registers a drop resolver + a drag-move
 *    feed; the inbox row's touch-drag calls `calendarTouchDragMove` each move
 *    (so a finger held at the grid's left/right edge PAGES the day window, the
 *    same as a block move) and `calendarTouchDrop` on release (maps the point to
 *    a day + slot and schedules).
 *  - calendar → inbox: the inbox panel (while open as the mobile drawer)
 *    registers an inbox drop target; the calendar's long-press block move
 *    consults `pointOverInbox` during the drag (to suppress edge-paging over the
 *    inbox) and `inboxTouchDrop` on release (un-schedule if dropped on it).
 *
 * Plain module singletons (one timed grid / one inbox drawer mounted at a time)
 * avoid threading handlers down through every calendar route.
 */
type CalendarDropFn = (taskId: string, clientX: number, clientY: number) => boolean;

let dropFn: CalendarDropFn | null = null;

export function setCalendarTouchDrop(fn: CalendarDropFn | null): void {
  dropFn = fn;
}

/** Returns true if the point was over the grid and the task was scheduled. */
export function calendarTouchDrop(taskId: string, clientX: number, clientY: number): boolean {
  return dropFn ? dropFn(taskId, clientX, clientY) : false;
}

// --- inbox→calendar drag-move feed (edge auto-paging while dragging) ---------

type DragMoveFn = (clientX: number, clientY: number) => void;
let dragMoveFn: DragMoveFn | null = null;
let dragEndFn: (() => void) | null = null;

export function setCalendarTouchDrag(move: DragMoveFn | null, end: (() => void) | null): void {
  dragMoveFn = move;
  dragEndFn = end;
}

/** Feed the live finger position so the grid can page days at its edges. */
export function calendarTouchDragMove(clientX: number, clientY: number): void {
  dragMoveFn?.(clientX, clientY);
}

/** Stop any edge-paging loop (drag released/cancelled). */
export function calendarTouchDragEnd(): void {
  dragEndFn?.();
}

// --- calendar→inbox drop target (block long-press → un-schedule) -------------

interface InboxTouchTarget {
  /** Is the point inside the (open) inbox panel? */
  hit: (clientX: number, clientY: number) => boolean;
  /** Drop a calendar block onto the inbox: un-schedule it. Returns true if the
   *  point was over the inbox and the drop was consumed (so the caller must NOT
   *  also reschedule it on the grid). */
  drop: (taskId: string, clientX: number, clientY: number) => boolean;
}

let inboxTarget: InboxTouchTarget | null = null;

export function setInboxTouchTarget(t: InboxTouchTarget | null): void {
  inboxTarget = t;
}

/** Is the inbox drawer currently open as a touch drop target? (A registered
 *  target ⇒ the drawer is mounted/open.) */
export function isInboxTouchTargetActive(): boolean {
  return inboxTarget !== null;
}

export function pointOverInbox(clientX: number, clientY: number): boolean {
  return inboxTarget?.hit(clientX, clientY) ?? false;
}

export function inboxTouchDrop(taskId: string, clientX: number, clientY: number): boolean {
  return inboxTarget?.drop(taskId, clientX, clientY) ?? false;
}
